import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {ContextualMenu} from '@Components/contextual-menu';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {TextListObject} from '@PosterWhiteboard/items/text-item/text-list';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import {getAssetUrl} from '@Utils/s3.util';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {COLOR_OBJECT_IDS} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import styles from './base-list-mobile-color-panel.module.scss';

interface BaseListMobileColorPanelProps {
  panelId: string;
  listObject: TextListObject;
  onChangeColor(color: RGBA[], undoable?: boolean): void;
  onChangeFillType(type: FillTypes): void;
  onClose(): void;
  onBack(): void;
}

enum PanelToShow {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/lists/';

export function BaseListMobileColorPanel({...props}: BaseListMobileColorPanelProps): ReactElement | null {
  const [panelToShow, setPanelToShow] = useState(PanelToShow.DEFAULT);
  const [selectedColorId, setSelectedColorId] = useState(0);

  useEffect((): void => {
    if (panelToShow === PanelToShow.COLOR_PICKER) {
      return;
    }
    let height = 228;

    if (props.listObject.fill.fillType === FillTypes.LINEAR_GRADIENT || props.listObject.fill.fillType === FillTypes.RADIAL_GRADIENT) {
      height += 16 + 32;
    }

    updateOptionsPanelHeight(props.panelId, `${height}px`);
  }, [props.listObject.fill.fillType, panelToShow]);

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        onBackButton={(): void => {
          setPanelToShow(PanelToShow.DEFAULT);
        }}
        panelId={props.panelId}
        fillObject={props.listObject.fill}
        onChangeColor={(color, undoable): void => {
          props.onChangeColor(color, undoable);
        }}
        onClose={(): void => {
          props.onClose();
        }}
        initialSelectedColorIndex={selectedColorId}
      />
    );
  };

  const getTextForColor = (index: number): string => {
    if (props.listObject.fill.fillType === FillTypes.SOLID) {
      return window.i18next.t('pmwjs_color');
    }

    if (index === 0) {
      return window.i18next.t('pmwjs_color_one');
    }
    return window.i18next.t('pmwjs_color_two');
  };

  const getColorOptions = (): ReactElement[] => {
    return props.listObject.fill.fillColor.map((listColor, index) => {
      return (
        <ContextualMenuColorFillOption
          key={`list-fill-color-${COLOR_OBJECT_IDS[index]}`}
          id={`list-fill-color-${COLOR_OBJECT_IDS[index]}`}
          label={getTextForColor(index)}
          className="spacing-m-b-4"
          data={[
            {
              id: `list-color-object-${COLOR_OBJECT_IDS[index]}`,
              color: getColorObjectArrayFromFillColor([listColor]),
            },
          ]}
          onClick={(): void => {
            setSelectedColorId(index);
            setPanelToShow(PanelToShow.COLOR_PICKER);
          }}
        />
      );
    });
  };

  const getTypeFromFillType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.LINEAR_GRADIENT:
        return styles.linear;
      case FillTypes.RADIAL_GRADIENT:
        return styles.radial;
      case FillTypes.SOLID:
      default:
        return styles.solid;
    }
  };

  const getThumbNameFromType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.LINEAR_GRADIENT:
        return 'linear.png';
      case FillTypes.RADIAL_GRADIENT:
        return 'radial.png';
      case FillTypes.SOLID:
      default:
        return 'solid.png';
    }
  };

  const getThumbnailFromType = (type: FillTypes): ReactElement => {
    if (props.listObject.type === LIST_TYPES.UNORDERED) {
      return <div className={`${styles.thumb} radius-round ${getTypeFromFillType(type)}`} />;
    }
    return (
      <Thumbnail
        className="spacing-p-l-4 spacing-p-r-4"
        data={{
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${getThumbNameFromType(type)}`),
        }}
      />
    );
  };

  const getThumbnails = (): ReactElement => {
    return (
      <ul className={`${styles.list} spacing-m-0 flex-h-row`}>
        <ContextualMenuThumbnailItem
          key="list-effect-solid"
          thumbnail={getThumbnailFromType(FillTypes.SOLID)}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_background_solid')}
          onClick={(): void => {
            props.onChangeFillType(FillTypes.SOLID);
          }}
          isSelected={props.listObject.fill.fillType === FillTypes.SOLID}
        />
        <ContextualMenuThumbnailItem
          key="list-effect-linear"
          thumbnail={getThumbnailFromType(FillTypes.LINEAR_GRADIENT)}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_linear_gradient_background')}
          isMultiLineText
          textClassName={styles.thumbnailText}
          onClick={(): void => {
            props.onChangeFillType(FillTypes.LINEAR_GRADIENT);
          }}
          isSelected={props.listObject.fill.fillType === FillTypes.LINEAR_GRADIENT}
        />
        <ContextualMenuThumbnailItem
          key="list-effect-radial"
          thumbnail={getThumbnailFromType(FillTypes.RADIAL_GRADIENT)}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_radial_gradient_background')}
          isMultiLineText
          textClassName={styles.thumbnailText}
          onClick={(): void => {
            props.onChangeFillType(FillTypes.RADIAL_GRADIENT);
          }}
          isSelected={props.listObject.fill.fillType === FillTypes.RADIAL_GRADIENT}
        />
      </ul>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="list-fill-color-option" hasMobileMorphingProperties hasMobileModalPadding>
        <div className="flexbox flex-column-reverse">{getColorOptions()}</div>
        {getThumbnails()}
      </ContextualMenu>
    );
  };

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_fill')}
        showBackButton
        onBackButton={(): void => {
          props.onBack();
        }}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case PanelToShow.COLOR_PICKER:
        return getColorPanel();
      case PanelToShow.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanels();
}
